import { apiFetch } from '@/lib/api';
import type {
  AdminFirm,
  AdminUser,
  AdminUserList,
  AdminUserUpdate,
  OutcomeQueue,
  OutcomeReview,
  OutcomeReviewIn,
} from '@/services/types';

/** Every account and what it can actually reach. Admin-only, 403 otherwise. */
export function fetchUsers(): Promise<AdminUserList> {
  return apiFetch<AdminUserList>('/api/v1/admin/users');
}

/**
 * Change one account's access.
 *
 * A PARTIAL update: only the fields passed are touched. Sending the whole object
 * back would clear `agencies` every time somebody toggled `is_active`, which is
 * exactly the accident this screen exists to prevent.
 */
export function updateUser(id: string, changes: AdminUserUpdate): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
}

/** A firm groups accounts whose client folders are shared among them. */
export function createFirm(name: string): Promise<AdminFirm> {
  return apiFetch<AdminFirm>('/api/v1/admin/firms', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

/** Put an account in a firm, or (null) take it out of every firm. */
export function setUserFirm(id: string, firmId: string | null): Promise<AdminUser> {
  return apiFetch<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(id)}/firm`, {
    method: 'PUT',
    body: JSON.stringify({ firm_id: firmId }),
  });
}

/** The CADE outcome-review queue: decisions whose code the ata's own words put in doubt. */
export function fetchOutcomeQueue(): Promise<OutcomeQueue> {
  return apiFetch<OutcomeQueue>('/api/v1/admin/cade/outcomes');
}

/** Accept a drafted proposal as it stands: it becomes the caller's review. */
export function acceptOutcomeProposal(decisionKey: string): Promise<OutcomeReview> {
  return apiFetch<OutcomeReview>(
    `/api/v1/admin/cade/outcomes/${encodeURIComponent(decisionKey)}/accept`,
    { method: 'POST' },
  );
}

/** Record a person's codes for one decision; the evidence must be copied from the clause. */
export function saveOutcomeReview(decisionKey: string, review: OutcomeReviewIn): Promise<OutcomeReview> {
  return apiFetch<OutcomeReview>(`/api/v1/admin/cade/outcomes/${encodeURIComponent(decisionKey)}`, {
    method: 'PUT',
    body: JSON.stringify(review),
  });
}
